// Página CustomerProfile - Perfil del cliente actual
// Permite a los clientes editar sus propios datos

import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { getCustomerByEmail, saveCustomer } from '../services/customers.service'
import Button from '../components/Button'
import Toast from '../components/Toast'
import { normalizeWhatsAppPhoneAR } from '../utils/whatsapp'
import './CustomerProfile.css'

/**
 * Página CustomerProfile
 * Permite a los clientes editar sus propios datos
 */
function CustomerProfile() {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    phones: [],
    primaryPhoneIndex: 0,
    addresses: [],
    primaryAddressIndex: 0,
    notes: ''
  })

  // Estado para nuevo teléfono
  const [newPhone, setNewPhone] = useState('')

  // Estado para nueva dirección
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    province: ''
  })

  const fetchCustomerData = useCallback(async () => {
    if (!currentUser?.email) return
    try {
      setLoading(true)
      const customer = await getCustomerByEmail(currentUser.email)
      
      if (customer) {
        setFormData({
          name: customer.name || '',
          phones: customer.phones || [],
          primaryPhoneIndex: customer.primaryPhoneIndex !== undefined ? customer.primaryPhoneIndex : 0,
          addresses: customer.addresses || [],
          primaryAddressIndex: customer.primaryAddressIndex !== undefined ? customer.primaryAddressIndex : 0,
          notes: customer.notes || ''
        })
      } else {
        // Si no existe, inicializar con datos básicos
        setFormData({
          name: currentUser.displayName || '',
          phones: [],
          primaryPhoneIndex: 0,
          addresses: [],
          primaryAddressIndex: 0,
          notes: ''
        })
      }
    } catch (error) {
      console.error('Error cargando datos del cliente:', error)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos del cliente'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  // Cargar datos del cliente
  useEffect(() => {
    if (currentUser?.email) {
      fetchCustomerData()
    }
  }, [currentUser, fetchCustomerData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    if (!formData.name.trim()) {
      setNotification({
        type: 'error',
        message: 'El nombre es requerido'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      await saveCustomer({
        email: currentUser.email,
        ...formData
      })
      
      setNotification({
        type: 'success',
        message: 'Perfil actualizado correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error actualizando perfil:', error)
      setNotification({
        type: 'error',
        message: 'Error al actualizar el perfil'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const addPhone = () => {
    const raw = newPhone.trim()
    if (raw) {
      const normalized = normalizeWhatsAppPhoneAR(raw)
      if (!normalized.isValid) {
        setNotification({
          type: 'error',
          message: 'El teléfono debe estar en formato WhatsApp 549XXXXXXXXXX'
        })
        setTimeout(() => setNotification(null), 3000)
        return
      }
      const newPhones = [...formData.phones, normalized.normalized]
      setFormData(prev => ({
        ...prev,
        phones: newPhones,
        primaryPhoneIndex: prev.phones.length === 0 ? 0 : prev.primaryPhoneIndex
      }))
      setNewPhone('')
    }
  }

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index)
    let newPrimaryIndex = formData.primaryPhoneIndex
    
    if (formData.primaryPhoneIndex === index) {
      newPrimaryIndex = newPhones.length > 0 ? 0 : 0
    } else if (formData.primaryPhoneIndex > index) {
      newPrimaryIndex = formData.primaryPhoneIndex - 1
    }
    
    setFormData(prev => ({
      ...prev,
      phones: newPhones,
      primaryPhoneIndex: newPrimaryIndex >= 0 ? newPrimaryIndex : 0
    }))
  }

  const setPrimaryPhone = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryPhoneIndex: index
    }))
  }

  const addAddress = () => {
    if (newAddress.street && newAddress.city && newAddress.province) {
      const newAddresses = [...formData.addresses, { ...newAddress }]
      setFormData(prev => ({
        ...prev,
        addresses: newAddresses,
        primaryAddressIndex: prev.addresses.length === 0 ? 0 : prev.primaryAddressIndex
      }))
      setNewAddress({
        street: '',
        city: '',
        postalCode: '',
        province: ''
      })
    }
  }

  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index)
    let newPrimaryIndex = formData.primaryAddressIndex
    
    if (formData.primaryAddressIndex === index) {
      newPrimaryIndex = newAddresses.length > 0 ? 0 : 0
    } else if (formData.primaryAddressIndex > index) {
      newPrimaryIndex = formData.primaryAddressIndex - 1
    }
    
    setFormData(prev => ({
      ...prev,
      addresses: newAddresses,
      primaryAddressIndex: newPrimaryIndex >= 0 ? newPrimaryIndex : 0
    }))
  }

  const setPrimaryAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      primaryAddressIndex: index
    }))
  }

  if (loading) {
    return (
      <div className="customer-profile-page">
        <div className="loading-state">
          <p>Cargando perfil...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="customer-profile-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="profile-header">
        <h1>Mi Perfil</h1>
        <p className="profile-subtitle">
          Gestiona tu información personal y de contacto
        </p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {/* Información básica */}
        <div className="form-section">
          <h2 className="section-title">Información Básica</h2>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={currentUser?.email || ''}
              disabled
              className="disabled-input"
            />
            <small className="form-hint">El email no se puede modificar</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">
              Nombre Completo <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Tu nombre completo"
              required
            />
          </div>
        </div>

        {/* Teléfonos */}
        <div className="form-section">
          <h2 className="section-title">Teléfonos</h2>
          
          {formData.phones.length > 0 && (
            <div className="phones-list">
              {formData.phones.map((phone, index) => (
                <div key={index} className="phone-item">
                  <div className="phone-content">
                    <span className="phone-text">{phone}</span>
                    {formData.primaryPhoneIndex === index && (
                      <span className="primary-badge">Principal</span>
                    )}
                  </div>
                  <div className="phone-actions">
                    {formData.primaryPhoneIndex !== index && (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setPrimaryPhone(index)}
                      >
                        Marcar como principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link btn-danger"
                      onClick={() => removePhone(index)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="new-phone-form">
            <div className="form-row">
              <div className="form-col">
                <input
                  type="tel"
                  placeholder="5493813333333"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addPhone()
                    }
                  }}
                />
              </div>
              <div className="form-col-auto">
                <button
                  type="button"
                  className="btn-add-phone"
                  onClick={addPhone}
                >
                  ➕ Agregar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Direcciones */}
        <div className="form-section">
          <h2 className="section-title">Direcciones de Envío</h2>
          
          {formData.addresses.length > 0 && (
            <div className="addresses-list">
              {formData.addresses.map((addr, index) => (
                <div key={index} className="address-item">
                  <div className="address-content">
                    <span className="address-text">
                      {addr.street}, {addr.city}, {addr.province} {addr.postalCode && `(${addr.postalCode})`}
                    </span>
                    {formData.primaryAddressIndex === index && (
                      <span className="primary-badge">Principal</span>
                    )}
                  </div>
                  <div className="address-actions">
                    {formData.primaryAddressIndex !== index && (
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => setPrimaryAddress(index)}
                      >
                        Marcar como principal
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-link btn-danger"
                      onClick={() => removeAddress(index)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="new-address-form">
            <h3 className="subsection-title">Agregar Nueva Dirección</h3>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Calle y número"
                  value={newAddress.street}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Ciudad"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Código Postal"
                  value={newAddress.postalCode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                />
              </div>
              <div className="form-col">
                <input
                  type="text"
                  placeholder="Provincia"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn-add-address"
              onClick={addAddress}
            >
              ➕ Agregar Dirección
            </button>
          </div>
        </div>

        {/* Notas */}
        <div className="form-section">
          <h2 className="section-title">Notas Personales</h2>
          <div className="form-group">
            <label htmlFor="notes">Notas / Observaciones</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas personales..."
              rows={4}
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CustomerProfile

